import type { ProviderProps, StepType } from '@reactour/tour'
import Group from 'components/utils/Group'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getLocaleDirection } from 'i18n/util/locale'
import {
  agentHubBookingOnboardingSelector,
  getVisibleAgentHubBookingOnboardingSteps,
} from './agentHubBookingOnboardingSteps'
import { useAgentHubBookingOnboardingCopy } from './useAgentHubBookingOnboardingCopy'

const ACCOUNT_STEP_POPOVER_SHIFT_X = 6

function shiftPopoverLeft(left: string | number | undefined, offset: number): string | number | undefined {
  if (typeof left === 'number') {
    return left + offset
  }

  if (typeof left === 'string' && left.endsWith('px')) {
    return `${parseFloat(left) + offset}px`
  }

  return left
}

function useAgentHubBookingOnboardingTourSteps() {
  const intl = useIntl()
  const copy = useAgentHubBookingOnboardingCopy()
  const includeTabletOnlySteps = useScreenSizeUp('tablet')
  // The popover position is physical, and the account menu sits on the opposite side of the header in RTL
  const accountStepPopoverShiftX = getLocaleDirection(intl.locale) === 'rtl' ? -ACCOUNT_STEP_POPOVER_SHIFT_X : ACCOUNT_STEP_POPOVER_SHIFT_X

  const skipLabel = intl.formatMessage({
    id: 'ZswB3-Ry8aDLPcD4qBwj',
    defaultMessage: 'Skip',
    description: 'Button that dismisses the Agent Hub booking platform onboarding walkthrough',
  })
  const backLabel = intl.formatMessage({
    id: '82LzhGADc-xp2MnEOBrK',
    defaultMessage: 'Back',
    description: 'Button that returns to the previous Agent Hub booking platform onboarding step',
  })
  const nextLabel = intl.formatMessage({
    id: 'LRsLp4S3jpgowh0cdO7a',
    defaultMessage: 'Next',
    description: 'Button that advances to the next Agent Hub booking platform onboarding step',
  })
  const doneLabel = intl.formatMessage({
    id: 'kP4DPV8x6S2y_YDhsEnM',
    defaultMessage: 'Done',
    description: 'Button that completes the Agent Hub booking platform onboarding walkthrough',
  })
  const closeLabel = intl.formatMessage({
    id: 'BAzgGlxcGND6eep39jZO',
    defaultMessage: 'Close walkthrough',
    description: 'Accessible label for closing the Agent Hub booking platform onboarding walkthrough',
  })

  const steps = useMemo<Array<StepType>>(() => (
    getVisibleAgentHubBookingOnboardingSteps(includeTabletOnlySteps).map((step) => {
      const selector = agentHubBookingOnboardingSelector(step.id)
      const stepCopy = copy[step.id]

      return {
        selector,
        resizeObservables: [selector],
        mutationObservables: [selector],
        padding: { mask: [4, 8] },
        ...(step.id === 'account' ? {
          styles: {
            popover: (base) => ({
              ...base,
              left: shiftPopoverLeft(base.left, accountStepPopoverShiftX),
            }),
          },
        } : {}),
        content: (
          <Group direction="vertical" gap={12}>
            <Heading id="agent-hub-booking-onboarding-title" variant="heading6">
              {stepCopy.title}
            </Heading>
            <BodyText id="agent-hub-booking-onboarding-body" variant="medium" colour="neutral-two">
              {stepCopy.body}
            </BodyText>
          </Group>
        ),
      }
    })
  ), [copy, includeTabletOnlySteps, accountStepPopoverShiftX])

  const badgeContent = useCallback<NonNullable<ProviderProps['badgeContent']>>(({ currentStep, totalSteps }) => (
    intl.formatMessage({
      id: 'y7r4CM7unAduA-K-W0cs',
      defaultMessage: 'Step {current} of {total}',
      description: 'Progress label showing the current Agent Hub booking platform onboarding step number',
    }, {
      current: currentStep + 1,
      total: totalSteps,
    })
  ), [intl])

  return {
    backLabel,
    badgeContent,
    closeLabel,
    doneLabel,
    nextLabel,
    skipLabel,
    steps,
  }
}

export default useAgentHubBookingOnboardingTourSteps
